import { useCallback, useEffect, useRef, useState } from "react"
import { motion, useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./AirtightPaywall.module.scss"

/**
 * AirtightPaywall (`/airtight`) — an original, self-contained FEATURE-SPECIFIC
 * paywall for Private Photo Vault Premium. Paywall type rolled at random this
 * run: FEATURE-SPECIFIC (one screen heroing one real capability).
 *
 * Divergence brief + full axes: claude-paywalls/_ledger.md. Concept doc:
 * claude-paywalls/airtight.md.
 *
 * ── Style directive (read live from #paywalls each run) ─────────────────────
 * Newest @Paywalls-Bot mention this run: `focus on security and privacy` (NOT
 * the reset keyword `default`) → an ACTIVE STANDING DIRECTIVE at super-high
 * priority. The whole concept is built to it: the pitch is SECURITY & PRIVACY,
 * reframed as the one property most people never think about — not who can see
 * your photos, but HOW MANY PLACES a single photo can quietly end up.
 *
 * ── Concept ────────────────────────────────────────────────────────────────
 * "One place. Nowhere else." Almost every prior privacy concept in this repo
 * asks "who can SEE in?" (Silvered's one-way mirror) or "how do we keep intruders
 * OUT?" (VaultDoor, Strongbox, Sentry). Airtight asks a different question: where
 * can a photo TRAVEL? A photo left in the open doesn't sit still — it copies
 * itself into your Camera Roll, iCloud Photos, Shared Albums, the Photos widget,
 * and every app's photo picker. That spread is the real exposure surface. The
 * hero is a VAULT CHAMBER with five real system surfaces fanned around it as
 * "leak channels". In the default state a single memory bleeds outward along
 * each channel — you can literally watch it get everywhere. You press "Seal the
 * vault": the leaks retract, every channel snaps shut, and the chamber pulls
 * airtight. The value is containment — your memories stay in exactly one place,
 * seen only by you.
 *
 * ── Divergence (vs. the 90+ prior concepts, incl. the security cluster) ─────
 * Hero motif  : a sealed AIRTIGHT CHAMBER with radiating leak channels — no prior
 *               concept is about a photo's distribution/spread across surfaces.
 * Value frame : EXPOSURE-SURFACE REDUCTION (attack surface) — "where can it go",
 *               not "who can see it" (Silvered), not "is it backed up" (Sealed),
 *               not intrusion (VaultDoor/Strongbox), not disguise (PlainSight).
 * Layout      : a hub-and-spoke pentagon — one chamber, five named system
 *               surfaces as satellites with live leak lines. Not a single pane
 *               (Silvered), not a keypad (OnlyYou), not a springboard (PlainSight).
 * Interaction : one decisive SEAL that retracts every leak at once and flips all
 *               five channels from "seen here" to "sealed". A collapse-inward, not
 *               a reveal-outward.
 * Behavioural : loss aversion (watch it spread) + contrast framing (leaky →
 *               airtight) + control. Register: relief from a low-grade dread.
 *
 * ── Honest & App Store compliant ───────────────────────────────────────────
 * The five channels are REAL system surfaces where a photo in the ordinary
 * Photos library shows up. Anything kept inside Private Photo Vault lives in the
 * app's private storage, so it genuinely never appears in any of them — that is
 * the true, defensible claim the animation dramatises. The "leaking" state is
 * labelled as an ordinary photo left in the open (not a claim the app deletes
 * copies already elsewhere). Proofs map to real features: only-you Face ID /
 * Touch ID / PIN, import removes the Camera Roll original, private encrypted
 * backup only you can open. No data-loss-on-expiration claim, no fake urgency or
 * scarcity, honest "Save %" from live product data. Yearly is the featured,
 * default, most prominent price; monthly is a small secondary line.
 */

type PlanKey = "yearly" | "monthly"

// Real personal-memory photos (bundled) for the chamber cluster + the leaking
// ghost. Never the user's own library — a demo set, shown in full colour.
const CHAMBER = ["m04", "m09", "m12", "m02", "m15", "m07"]
const LEAK_MEMORY = "m05"

// The five real system surfaces where an ordinary photo shows up. Positioned as
// a pentagon around the chamber (percentages of the stage box). `ax/ay` is where
// the leak line meets the chip (its inner edge), kept a touch off the chip centre
// so the line reads as feeding into it.
const CHANNELS = [
  { k: "roll", label: "Camera Roll", x: 50, y: 8, ax: 50, ay: 20 },
  { k: "icloud", label: "iCloud Photos", x: 88, y: 34, ax: 80, ay: 38 },
  { k: "widget", label: "Photos widget", x: 79, y: 90, ax: 71, ay: 80 },
  { k: "shared", label: "Shared Albums", x: 21, y: 90, ax: 29, ay: 80 },
  { k: "picker", label: "App pickers", x: 12, y: 34, ax: 20, ay: 38 },
]

const AirtightPaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$2.99"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"

  const savingsPct = (() => {
    const y = yearlyProduct?.price ?? 35.99
    const m = monthlyProduct?.price ?? 13.99
    if (!m) return 79
    return Math.round((1 - y / (m * 12)) * 100)
  })()

  const [sealed, setSealed] = useState<boolean>(!!reduceMotion)
  const [plan, setPlan] = useState<PlanKey>("yearly")
  const [showClose, setShowClose] = useState(false)
  const [hint, setHint] = useState(false)

  const touched = useRef(false)
  const timers = useRef<number[]>([])
  const pushTimer = (id: number) => timers.current.push(id)

  const seal = useCallback(() => {
    setSealed(true)
    native.logMixpanelEvent("html_airtight_seal")
  }, [])

  // Auto-demo for passive viewers: let the leak breathe, then seal it airtight so
  // the payoff is always seen. Reduced motion starts sealed and static.
  useEffect(() => {
    if (reduceMotion) {
      setShowClose(true)
      return
    }
    pushTimer(window.setTimeout(() => setHint(true), 800))
    pushTimer(window.setTimeout(() => setShowClose(true), 2000))
    pushTimer(
      window.setTimeout(() => {
        if (!touched.current) seal()
      }, 3000),
    )
    return () => {
      timers.current.forEach((id) => window.clearTimeout(id))
      timers.current = []
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSeal = () => {
    touched.current = true
    setHint(false)
    seal()
  }

  const selectedProduct = plan === "yearly" ? yearlyProduct : monthlyProduct
  const handleClose = () => native.postNativeEvent("close")
  const handleSubscribe = () => {
    native.logMixpanelEvent("html_airtight_subscribe", { plan })
    if (selectedProduct) native.subscribe(selectedProduct)
  }
  const openTerms = () => native.postNativeEvent("terms")
  const openPrivacy = () => native.postNativeEvent("privacy")

  return (
    <div className={`${styles.root} ${sealed ? styles.rootSealed : ""}`}>
      <div className={styles.aura} aria-hidden="true" />

      {/* ---------- header ---------- */}
      <header className={styles.top}>
        <div className={styles.brand}>
          <span className={styles.brandMark} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="15" height="15">
              <path
                d="M12 2.6l6.6 2.5v5.2c0 4.3-2.8 7.7-6.6 9.1-3.8-1.4-6.6-4.8-6.6-9.1V5.1L12 2.6z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinejoin="round"
              />
              <circle cx="12" cy="11.4" r="2.1" fill="currentColor" />
            </svg>
          </span>
          <span className={styles.brandText}>
            Private Photo Vault
            <em>Premium</em>
          </span>
        </div>
        <button
          className={`${styles.close} ${showClose ? styles.closeOn : ""}`}
          onClick={handleClose}
          aria-label="Close"
          tabIndex={showClose ? 0 : -1}
        >
          <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
            <path
              d="M5 5l14 14M19 5L5 19"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </header>

      {/* ---------- title ---------- */}
      <div className={styles.intro}>
        <span className={styles.eyebrow}>Feature · Airtight vault</span>
        <h1 className={styles.title}>
          One photo can end up in five places.
          <br />
          <span className={styles.titleAccent}>Here, it stays in one.</span>
        </h1>
        <p className={styles.sub}>
          Keep a memory in your vault and it never lands in your Camera Roll,
          iCloud, shared albums, widgets, or any app’s picker — one place, seen
          only by you.
        </p>
      </div>

      {/* ---------- the chamber stage ---------- */}
      <div className={styles.stage}>
        {/* leak lines: draw from chamber centre out to each channel. They flow
            outward while leaky, and retract when sealed. */}
        <svg
          className={styles.leaks}
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          {CHANNELS.map((c) => (
            <line
              key={c.k}
              className={styles.leak}
              x1="50"
              y1="50"
              x2={c.ax}
              y2={c.ay}
              vectorEffect="non-scaling-stroke"
            />
          ))}
        </svg>

        {/* the channels — real system surfaces */}
        {CHANNELS.map((c, i) => (
          <div
            key={c.k}
            className={styles.channel}
            style={
              {
                left: `${c.x}%`,
                top: `${c.y}%`,
                ["--d" as any]: `${i * 90}ms`,
              } as React.CSSProperties
            }
          >
            <span className={styles.channelGhost} aria-hidden="true">
              <span
                className={styles.channelGhostImg}
                style={{ backgroundImage: `url(/byheart/${LEAK_MEMORY}.jpg)` }}
              />
              <span className={styles.channelSeal} aria-hidden="true">
                <svg viewBox="0 0 24 24" width="11" height="11">
                  <path
                    d="M5 12.5l4 4 10-10.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </span>
            <span className={styles.channelLabel}>{c.label}</span>
            <span className={styles.channelState}>
              {sealed ? "Not here" : "Seen here"}
            </span>
          </div>
        ))}

        {/* the chamber — the vault itself */}
        <div
          className={`${styles.chamber} ${hint ? styles.chamberHint : ""}`}
          role="group"
          aria-label="Your vault"
        >
          <span className={styles.ring} aria-hidden="true" />
          <span className={styles.ringSolid} aria-hidden="true" />
          <div className={styles.cluster}>
            {CHAMBER.map((m, i) => (
              <span
                key={m}
                className={styles.tile}
                style={{ transitionDelay: `${i * 40}ms` }}
              >
                <span
                  className={styles.tileImg}
                  style={{ backgroundImage: `url(/byheart/${m}.jpg)` }}
                />
              </span>
            ))}
          </div>
          <span className={styles.chamberBadge} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="15" height="15">
              {sealed ? (
                <path
                  d="M7 10V8a5 5 0 0110 0v2M6 10h12v9H6z"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.9"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              ) : (
                <path
                  d="M7 10V8a5 5 0 019.5-2M6 10h12v9H6z"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.9"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              )}
            </svg>
          </span>
        </div>
      </div>

      {/* readout */}
      <div className={styles.readout} aria-live="polite">
        <span
          className={`${styles.readoutDot} ${sealed ? styles.readoutDotOn : ""}`}
          aria-hidden="true"
        />
        <span className={styles.readoutText}>
          {sealed
            ? "Airtight — one place, seen only by you"
            : "Left in the open — this photo is in 5 places"}
        </span>
      </div>

      {/* ---------- seal control ---------- */}
      <div className={styles.control}>
        {sealed ? (
          <span className={styles.settled}>
            <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
              <path
                d="M5 12.5l4 4 10-10.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Sealed. Your memories stay put — and go nowhere else.
          </span>
        ) : (
          <button className={styles.sealBtn} onClick={handleSeal}>
            <span className={styles.sealDot} aria-hidden="true" />
            Seal the vault
          </button>
        )}
      </div>

      {/* ---------- proofs (real features) ---------- */}
      <div className={styles.proofs}>
        <span className={styles.proofsLead}>
          Airtight is how it feels. Here’s what makes it real:
        </span>
        <div className={styles.proofList}>
          {[
            "Only-you unlock — Face ID, Touch ID or PIN",
            "Importing pulls the original out of your Camera Roll",
            "Private encrypted backup only you can open",
          ].map((p, i) => (
            <span
              key={p}
              className={`${styles.proof} ${sealed ? styles.proofOn : ""}`}
              style={{ transitionDelay: `${140 + i * 90}ms` }}
            >
              <svg
                viewBox="0 0 24 24"
                width="13"
                height="13"
                aria-hidden="true"
              >
                <path
                  d="M5 12.5l4 4 10-10.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {p}
            </span>
          ))}
        </div>
      </div>

      {/* ---------- pricing (yearly featured) ---------- */}
      <div className={styles.pricing}>
        <button
          className={`${styles.plan} ${styles.planYear} ${
            plan === "yearly" ? styles.planActive : ""
          }`}
          onClick={() => setPlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          <span className={styles.ribbon}>SAVE {savingsPct}%</span>
          <span className={styles.planLeft}>
            <span className={styles.radio} aria-hidden="true" />
            <span className={styles.planMeta}>
              <span className={styles.planName}>12 months</span>
              <span className={styles.planNote}>
                7 days free · then {yearlyPerMonth}/mo
              </span>
            </span>
          </span>
          <span className={styles.planRight}>
            <span className={styles.planPrice}>{yearlyPrice}</span>
            <span className={styles.planUnit}>/year</span>
          </span>
        </button>

        <button
          className={`${styles.plan} ${styles.planMonth} ${
            plan === "monthly" ? styles.planActive : ""
          }`}
          onClick={() => setPlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span className={styles.planLeft}>
            <span className={styles.radio} aria-hidden="true" />
            <span className={styles.planName}>Monthly</span>
          </span>
          <span className={styles.planMonthPrice}>{monthlyPrice}/mo</span>
        </button>
      </div>

      <motion.button
        className={styles.cta}
        onClick={handleSubscribe}
        whileTap={{ scale: 0.985 }}
      >
        <span className={styles.ctaMain}>
          {plan === "yearly" ? "Try 7 days free" : "Continue"}
        </span>
        <span className={styles.ctaSub}>
          {plan === "yearly"
            ? `Then ${yearlyPrice}/year · cancel anytime`
            : `${monthlyPrice}/month · cancel anytime`}
        </span>
      </motion.button>

      <div className={styles.legal}>
        <button onClick={openTerms}>Terms</button>
        <span aria-hidden="true">·</span>
        <button onClick={openPrivacy}>Privacy</button>
        <span aria-hidden="true">·</span>
        <span>Auto-renews until cancelled</span>
      </div>
    </div>
  )
}

export default AirtightPaywall
